import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { getProductCategoriesAsync } from "../../../../slices/productCategory";
import { CategoryTreeView } from "../../../../components/dashboard/products/category-tree-view";
import CategoryDialog from "../../../../components/dashboard/products/category-dialog";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import { updateUrlWithException } from "../../../../components/Utils";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { FolderOpenIcon } from "@heroicons/react/24/outline";

export const ProductCategoriesList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const categoryState = useSelector((store) => {
    return store.productCategories || { data: [], isLoading: false, totalCount: 0 };
  });

  const { isLoading, data: categories } = categoryState;
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [controller, setController] = useState({
    filters: [],
    per_page: 1000, // Get all categories for tree view
    page: 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "title",
    parents: false, // Get all categories, not just parents
  });

  // Update URL params from controller
  useEffect(() => {
    const paramsToUpdate = {
      page: controller.page,
      sort: controller.sort,
      sortBy: controller.sortBy,
    };
    
    // Only include query if it's not empty
    if (controller.query && controller.query.trim() !== "") {
      paramsToUpdate.query = controller.query;
    }
    
    updateUrlWithException(paramsToUpdate, "filters");
  }, [controller]);

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle any future filter types here
    // For now, we'll just use the query filter

    setController({
      ...controller,
      page: 1,
      query: filters.query || controller.query,
      filters: backendFilters,
    });
  };

  const debouncedDispatchAction = debounce((controller) => {
    dispatch(getProductCategoriesAsync(controller));
  }, 250);

  useEffect(() => {
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setCategoryDialogOpen(true);
  };

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setCategoryDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCategoryDialogOpen(false);
    setSelectedCategory(null);
    // No refresh needed - Redux slice automatically updates the list
  };

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
    };

    return initialFilters;
  };

  // Filter categories based on search query
  const filteredCategories = categories.filter((category) => {
    if (!controller.query) return true;
    const query = controller.query.toLowerCase();
    const title = (category.displayTitle || category.title || "").toLowerCase();
    const description = (category.description || "")
      .replace(/<[^>]*>/g, "")
      .toLowerCase();
    return title.includes(query) || description.includes(query);
  });

  const headerActions = [{}];

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={t("Categories")}
        meta={[]}
        mainButton={{
          label: t("New Category"),
          action: handleCreateCategory,
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder={t("Search categories...")}
              filterTitle={t("Filter Categories")}
              hideStatusFilter={true}
            />

            <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FolderOpenIcon className="w-6 h-6 text-slate-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t("Category Tree")}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t("All product categories organized in a tree structure")}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {filteredCategories.length} {t("categories")}
                  </div>
                </div>
              </div>
              <div className="bg-white px-6 py-6">
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-500">{t("Loading categories...")}</p>
                  </div>
                ) : (
                  <CategoryTreeView
                    categories={filteredCategories}
                    onEdit={handleEditCategory}
                    expandedNodes={expandedNodes}
                    onToggleExpand={setExpandedNodes}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardHeader>

      <CategoryDialog
        open={categoryDialogOpen}
        onClose={handleDialogClose}
        selected={selectedCategory}
        readOnly={false}
      />
    </>
  );
};

